import { z } from 'zod';
import {
  ADD_YOUR_SCRIPT_PROMPT_USED_IN,
  PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN,
  PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN,
} from '../events/persona-opportunities.js';
import { CHANGELOG_PROMPT_DEPLOY_NOTES, CHANGELOG_PROMPT_ROUNDUP } from './changelog-prompts.js';
import {
  SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL,
  SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER,
  SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION,
  SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH,
  SCRIPT_REVIEW_PROMPT_SUGGESTION,
  SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY,
  SCRIPT_REVIEW_PROMPT_TRANSCRIPT,
  SCRIPT_REVIEW_PROMPT_VISUAL,
  SCRIPT_REVIEW_PROMPT_VOICEOVER,
} from './script-review-prompts.js';
import {
  PromptTaskTypeSchema,
  derivePromptModelCapabilities,
  type PromptTaskType,
} from './prompt-task-types.js';

// ─── Catalog DTO ─────────────────────────────────────────────────────

export const PromptLlmModelTierSchema = z.enum(['economy', 'standard', 'premium']);
export type PromptLlmModelTier = z.infer<typeof PromptLlmModelTierSchema>;

export const PromptLlmModelProviderSchema = z.enum(['anthropic', 'google']);
export type PromptLlmModelProvider = z.infer<typeof PromptLlmModelProviderSchema>;

export const PromptLlmModelFamilySchema = z.enum(['claude', 'gemini']);
export type PromptLlmModelFamily = z.infer<typeof PromptLlmModelFamilySchema>;

export const PromptLlmModelCostHintSchema = z.enum(['$', '$$', '$$$']);
export type PromptLlmModelCostHint = z.infer<typeof PromptLlmModelCostHintSchema>;

export const PromptLlmModelSchema = z.object({
  id: z.string().min(1).max(100),
  label: z.string().min(1),
  provider: PromptLlmModelProviderSchema,
  family: PromptLlmModelFamilySchema,
  tier: PromptLlmModelTierSchema,
  costHint: PromptLlmModelCostHintSchema,
  capabilities: z.array(PromptTaskTypeSchema).min(1),
  /**
   * Vertex advertises this id but the project cannot invoke it. Such a model is
   * still listed, locked, so "why is Opus 5 missing?" has an answer in the UI
   * instead of in someone's head. Absent means usable.
   */
  unavailable: z.boolean().optional(),
  /** List price from `llm_model_prices`, micro-dollars per million tokens. */
  inputPerMillion: z.number().int().nonnegative().nullable().optional(),
  outputPerMillion: z.number().int().nonnegative().nullable().optional(),
});
export type PromptLlmModel = z.infer<typeof PromptLlmModelSchema>;

export const PromptLlmModelsResponseSchema = z.object({
  models: z.array(PromptLlmModelSchema),
  /** Soft cache age marker for UI freshness hints. */
  fetchedAt: z.string().datetime().nullable(),
  /** True when serving bootstrap fallback because Vertex returned nothing usable. */
  fromBootstrap: z.boolean(),
});
export type PromptLlmModelsResponse = z.infer<typeof PromptLlmModelsResponseSchema>;

// ─── Family helpers ──────────────────────────────────────────────────

export function promptModelFamily(modelId: string): PromptLlmModelFamily | null {
  const id = modelId.trim().toLowerCase();
  if (id.startsWith('claude-')) return 'claude';
  if (id.startsWith('gemini-')) return 'gemini';
  return null;
}

export function isClaudePromptModel(modelId: string | null | undefined): boolean {
  return modelId != null && promptModelFamily(modelId) === 'claude';
}

export function isGeminiPromptModel(modelId: string | null | undefined): boolean {
  return modelId != null && promptModelFamily(modelId) === 'gemini';
}

/**
 * Prefer a saved prompt model when its family matches the call site.
 * Otherwise return the service fallback (expand/contract).
 */
export function resolvePromptModelForFamily(
  promptModel: string | null | undefined,
  expectedFamily: PromptLlmModelFamily,
  fallbackModel: string,
): string {
  if (promptModel && promptModelFamily(promptModel) === expectedFamily) {
    return promptModel;
  }
  return fallbackModel;
}

// ─── Tier heuristics (id-based; not Vertex pricing) ─────────────────

const PREMIUM_TIER_SUBSTRINGS = ['opus', 'fable', 'mythos', 'pro', 'ultra'];
const ECONOMY_TIER_SUBSTRINGS = ['flash-lite', 'haiku'];

/** Flash and Sonnet fall through to standard. */
export function derivePromptModelTier(modelId: string): PromptLlmModelTier {
  const id = modelId.toLowerCase();
  if (PREMIUM_TIER_SUBSTRINGS.some((substring) => id.includes(substring))) return 'premium';
  if (ECONOMY_TIER_SUBSTRINGS.some((substring) => id.includes(substring))) return 'economy';
  return 'standard';
}

export function costHintForTier(tier: PromptLlmModelTier): PromptLlmModelCostHint {
  if (tier === 'economy') return '$';
  if (tier === 'premium') return '$$$';
  return '$$';
}

// ─── Bootstrap fallback (prod IDs Nexus already runs) ────────────────

function bootstrapModel(
  id: string,
  label: string,
  provider: PromptLlmModelProvider,
  family: PromptLlmModelFamily,
  capabilities?: readonly PromptTaskType[],
): PromptLlmModel {
  const tier = derivePromptModelTier(id);
  const resolved = capabilities ? [...capabilities] : derivePromptModelCapabilities(id);
  if (resolved.length === 0) {
    throw new Error(`Bootstrap model ${id} has no capabilities`);
  }
  return {
    id,
    label,
    provider,
    family,
    tier,
    costHint: costHintForTier(tier),
    capabilities: resolved,
  };
}

export const PROMPT_LLM_BOOTSTRAP_MODELS: readonly PromptLlmModel[] = [
  bootstrapModel('claude-opus-5-5', 'Claude Opus 5.5', 'anthropic', 'claude'),
  bootstrapModel('claude-sonnet-5', 'Claude Sonnet 5', 'anthropic', 'claude'),
  bootstrapModel('claude-opus-4-6', 'Claude Opus 4.6', 'anthropic', 'claude'),
  bootstrapModel('gemini-3.8-flash', 'Gemini 3.8 Flash', 'google', 'gemini'),
  bootstrapModel('gemini-3.5-flash-lite', 'Gemini 3.5 Flash Lite', 'google', 'gemini'),
  bootstrapModel('gemini-3-pro-image', 'Gemini 3 Pro Image', 'google', 'gemini'),
  bootstrapModel('gemini-3.1-flash-lite-image', 'Gemini 3.1 Flash Lite Image', 'google', 'gemini'),
  bootstrapModel('gemini-nano-banana-2.1', 'Nano Banana 2.1', 'google', 'gemini', ['image']),
];

/**
 * Bootstrap catalog capabilities win over the id heuristic. Ids such as
 * `gemini-nano-banana-2.1` have no `-image` suffix, so derivation would mark
 * them as text and vision.
 */
export function modelSupportsCatalogTask(modelId: string, taskType: PromptTaskType): boolean {
  const listed = PROMPT_LLM_BOOTSTRAP_MODELS.find((model) => model.id === modelId);
  const capabilities = listed ? listed.capabilities : derivePromptModelCapabilities(modelId);
  return capabilities.includes(taskType);
}

/**
 * Prefer a saved prompt model when the catalog says it supports the call
 * site's task. Bootstrap capabilities win over the id heuristic, the same
 * check as save-time validation. Otherwise return the service fallback.
 */
export function resolvePromptModelForTask(
  promptModel: string | null | undefined,
  taskType: PromptTaskType,
  fallbackModel: string,
): string {
  if (promptModel && modelSupportsCatalogTask(promptModel, taskType)) {
    return promptModel;
  }
  return fallbackModel;
}

// ─── Smart suggestions by real usedIn ────────────────────────────────

export const PromptModelSuggestionSchema = z.object({
  provider: PromptLlmModelProviderSchema,
  recommendedTier: PromptLlmModelTierSchema,
  suggestedModelId: z.string().min(1).max(100).optional(),
  reason: z.string().min(1).max(240).optional(),
});
export type PromptModelSuggestion = z.infer<typeof PromptModelSuggestionSchema>;

const CLAUDE_PREMIUM: PromptModelSuggestion = {
  provider: 'anthropic',
  recommendedTier: 'premium',
  suggestedModelId: 'claude-opus-5-5',
  reason: 'Client-facing copy; keep premium.',
};

const GEMINI_STANDARD: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'standard',
  suggestedModelId: 'gemini-3.8-flash',
  reason: 'Extraction and structured passes; a standard model is enough.',
};

const GEMINI_IMAGE_PREMIUM: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'premium',
  suggestedModelId: 'gemini-3-pro-image',
  reason: 'Image output quality matters for avatars.',
};

const GEMINI_IMAGE_ECONOMY: PromptModelSuggestion = {
  provider: 'google',
  recommendedTier: 'economy',
  suggestedModelId: 'gemini-3.1-flash-lite-image',
  reason: 'High-volume storyboard frames; favor economy.',
};

/**
 * Per-usedIn model guidance for the Prompts editor.
 * Seeded from call-site defaults — not from PROMPT_USED_IN_SUGGESTIONS (legacy UI labels).
 * Call sites that ignore the prompt model (`PROMPT_SUPPORTED_FAMILIES` maps them to `[]`) get
 * no entry: the editor pre-selects the suggestion, and the API rejects any model there.
 */
export const PROMPT_MODEL_SUGGESTIONS: Readonly<Record<string, PromptModelSuggestion>> = {
  'creative-script-generation-angles': CLAUDE_PREMIUM,
  'creative-script-generation-topics': CLAUDE_PREMIUM,
  'creative-script-generation-custom': CLAUDE_PREMIUM,
  [ADD_YOUR_SCRIPT_PROMPT_USED_IN]: CLAUDE_PREMIUM,
  'creative-script-generation-persona-angles': CLAUDE_PREMIUM,
  'creative-brief-generation-persona-angles': CLAUDE_PREMIUM,
  [PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN]: CLAUDE_PREMIUM,
  'creative-brief-edit-persona-angles': CLAUDE_PREMIUM,
  'creative-script-hook-variations-persona': CLAUDE_PREMIUM,
  'creative-storyboard-beat-extraction': GEMINI_STANDARD,
  'creative-storyboard-image': GEMINI_IMAGE_ECONOMY,
  'persona-avatar-generation': GEMINI_IMAGE_PREMIUM,
  [PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_TRANSCRIPT]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_VOICEOVER]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_TEXT_OVERLAY]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_VISUAL]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_EXTRACTION]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_SUGGESTION]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_SCRIPT_MATCH]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VOICEOVER]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_ITERATION_TEXT_OVERLAY]: GEMINI_STANDARD,
  [SCRIPT_REVIEW_PROMPT_ITERATION_VISUAL]: GEMINI_STANDARD,
  [CHANGELOG_PROMPT_DEPLOY_NOTES]: GEMINI_STANDARD,
  [CHANGELOG_PROMPT_ROUNDUP]: GEMINI_STANDARD,
};

export function getPromptModelSuggestion(usedIn: string): PromptModelSuggestion | null {
  return PROMPT_MODEL_SUGGESTIONS[usedIn] ?? null;
}

const TIER_RANK: Record<PromptLlmModelTier, number> = {
  economy: 0,
  standard: 1,
  premium: 2,
};

export function comparePromptModelTiers(a: PromptLlmModelTier, b: PromptLlmModelTier): number {
  return TIER_RANK[a] - TIER_RANK[b];
}

/**
 * Resolve a suggestion against the live catalog: prefer suggestedModelId if still
 * listed and capable; otherwise newest matching provider + recommendedTier that
 * supports the required task type (when provided).
 */
export function resolvePromptModelSuggestion(
  usedIn: string,
  catalog: readonly PromptLlmModel[],
  taskType?: PromptTaskType,
): PromptLlmModel | null {
  const suggestion = getPromptModelSuggestion(usedIn);
  if (!suggestion) return null;

  const capable = (m: PromptLlmModel) =>
    taskType == null || m.capabilities.includes(taskType);

  if (suggestion.suggestedModelId) {
    const exact = catalog.find((m) => m.id === suggestion.suggestedModelId && capable(m));
    if (exact) return exact;
  }

  const matches = catalog.filter(
    (m) =>
      m.provider === suggestion.provider &&
      m.tier === suggestion.recommendedTier &&
      capable(m),
  );
  if (matches.length === 0) return null;

  // Prefer lexicographically last id as a crude "newest" heuristic for versioned ids.
  return [...matches].sort((a, b) => a.id.localeCompare(b.id)).at(-1) ?? null;
}
